/**
 * TornadoChart Component
 *
 * Sensitivity analysis visualization showing which factors
 * have the largest impact on outcomes (volume, share, etc.)
 *
 * TASK-2026-071: Scenario Sandbox - Phase 1 Trust & Workflow
 */

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';

export interface SensitivityFactor {
  name: string;
  lowValue: number;   // Impact when factor is at low end
  highValue: number;  // Impact when factor is at high end
  baseValue: number;  // Base case value
  unit?: string;      // e.g., '%', 'units', '$'
}

interface TornadoChartProps {
  factors: SensitivityFactor[];
  baselineValue: number;
  title?: string;
  valueLabel?: string;
  className?: string;
}

export function TornadoChart({
  factors,
  baselineValue,
  title = 'Sensitivity Analysis',
  valueLabel = 'Impact on Volume',
  className = '',
}: TornadoChartProps) {
  // Sort factors by total range (most sensitive first)
  const sortedFactors = [...factors].sort((a, b) => {
    const rangeA = Math.abs(a.highValue - a.lowValue);
    const rangeB = Math.abs(b.highValue - b.lowValue);
    return rangeB - rangeA;
  });

  // Transform data for the tornado chart
  // Each bar shows deviation from baseline
  const chartData = sortedFactors.map((factor) => ({
    name: factor.name,
    low: factor.lowValue - baselineValue,
    high: factor.highValue - baselineValue,
    lowAbsolute: factor.lowValue,
    highAbsolute: factor.highValue,
    unit: factor.unit || '',
  }));

  const maxAbsValue = Math.max(
    ...chartData.flatMap((d) => [Math.abs(d.low), Math.abs(d.high)])
  );

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 ${className}`}>
      <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
        {title}
      </h4>
      <p className="text-sm text-text-tertiary dark:text-text-secondary mb-4">
        {valueLabel} (baseline: {baselineValue.toLocaleString()})
      </p>

      <div style={{ width: '100%', height: Math.max(300, factors.length * 50) }}>
        <ResponsiveContainer>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 20, right: 40, left: 120, bottom: 20 }}
          >
            <XAxis
              type="number"
              domain={[-maxAbsValue * 1.1, maxAbsValue * 1.1]}
              tickFormatter={(value) => {
                const sign = value >= 0 ? '+' : '';
                return `${sign}${value.toFixed(0)}`;
              }}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 12, fill: '#6b7280' }}
              width={110}
            />
            <Tooltip content={<TornadoTooltip baselineValue={baselineValue} />} />
            <ReferenceLine x={0} stroke="#9ca3af" strokeWidth={2} />

            {/* Low value bar (left side - typically negative) */}
            <Bar dataKey="low" stackId="tornado" name="Low Scenario">
              {chartData.map((entry, index) => (
                <Cell
                  key={`low-${index}`}
                  fill={entry.low < 0 ? '#ef4444' : '#22c55e'}
                  radius={(entry.low < 0 ? [4, 0, 0, 4] : [0, 4, 4, 0]) as unknown as number}
                />
              ))}
            </Bar>

            {/* High value bar (right side - typically positive) */}
            <Bar dataKey="high" stackId="tornado" name="High Scenario">
              {chartData.map((entry, index) => (
                <Cell
                  key={`high-${index}`}
                  fill={entry.high > 0 ? '#22c55e' : '#ef4444'}
                  radius={(entry.high > 0 ? [0, 4, 4, 0] : [4, 0, 0, 4]) as unknown as number}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-500 rounded" />
          <span className="text-text-secondary dark:text-text-secondary">Negative Impact</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-500 rounded" />
          <span className="text-text-secondary dark:text-text-secondary">Positive Impact</span>
        </div>
      </div>
    </div>
  );
}

function TornadoTooltip({
  active,
  payload,
  label,
  baselineValue,
}: {
  active?: boolean;
  payload?: Array<{ value: number; dataKey: string; payload: Record<string, unknown> }>;
  label?: string;
  baselineValue: number;
}) {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0].payload;
  const lowAbs = data.lowAbsolute as number;
  const highAbs = data.highAbsolute as number;
  const unit = data.unit as string;

  return (
    <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-3 border border-gray-200 dark:border-gray-700">
      <p className="font-medium text-gray-900 dark:text-white mb-2">{label}</p>
      <div className="space-y-1 text-sm">
        <div className="flex justify-between gap-4">
          <span className="text-text-tertiary dark:text-text-secondary">Low scenario:</span>
          <span className="font-medium text-red-600 dark:text-red-400">
            {lowAbs.toLocaleString()}{unit}
          </span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-text-tertiary dark:text-text-secondary">Baseline:</span>
          <span className="font-medium text-gray-900 dark:text-white">
            {baselineValue.toLocaleString()}{unit}
          </span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-text-tertiary dark:text-text-secondary">High scenario:</span>
          <span className="font-medium text-green-600 dark:text-green-400">
            {highAbs.toLocaleString()}{unit}
          </span>
        </div>
        <div className="border-t border-gray-200 dark:border-gray-600 pt-1 mt-1">
          <div className="flex justify-between gap-4">
            <span className="text-text-tertiary dark:text-text-secondary">Range:</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {Math.abs(highAbs - lowAbs).toLocaleString()}{unit}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper to generate sensitivity factors from simulation config
export function generateSensitivityFactors(
  baseResult: number,
  config: Record<string, unknown>
): SensitivityFactor[] {
  // Default sensitivity ranges (±20% for most factors)
  const defaultFactors: SensitivityFactor[] = [
    {
      name: 'Price',
      lowValue: baseResult * 1.15,  // Lower price = higher volume
      highValue: baseResult * 0.85,
      baseValue: baseResult,
    },
    {
      name: 'Distribution',
      lowValue: baseResult * 0.70,
      highValue: baseResult * 1.20,
      baseValue: baseResult,
    },
    {
      name: 'Awareness',
      lowValue: baseResult * 0.75,
      highValue: baseResult * 1.15,
      baseValue: baseResult,
    },
    {
      name: 'Trial Rate',
      lowValue: baseResult * 0.80,
      highValue: baseResult * 1.25,
      baseValue: baseResult,
    },
    {
      name: 'Repeat Rate',
      lowValue: baseResult * 0.85,
      highValue: baseResult * 1.20,
      baseValue: baseResult,
    },
    {
      name: 'Competitive Response',
      lowValue: baseResult * 0.90,
      highValue: baseResult * 1.05,
      baseValue: baseResult,
    },
  ];

  return defaultFactors;
}
